import { useEffect, useRef, useState, useCallback } from "react";
import { getToken, API_BASE } from "@/lib/api";

/**
 * Real-time chat via WebSocket + typing indicators.
 * Returns { messages, sendMessage, sendTyping, connected, typingUsers }
 */
export function useChatSocket(requestId) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { userId: {name, until} }
  const wsRef = useRef(null);
  const typingTimersRef = useRef({});
  const reconnectRef = useRef(null);

  const connect = useCallback(() => {
    if (!requestId) return;
    const token = getToken();
    if (!token) return;
    const base = API_BASE.replace(/^http/, "ws");
    const url = `${base}/ws/chat/${requestId}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // basic reconnect after 2s
      reconnectRef.current = setTimeout(connect, 2000);
    };
    ws.onerror = () => {
      /* onclose will fire */
    };
    ws.onmessage = (ev) => {
      let evt;
      try {
        evt = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (evt.type === "history") {
        setMessages(evt.messages || []);
      } else if (evt.type === "message") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === evt.message.id)) return prev;
          return [...prev, evt.message];
        });
      } else if (evt.type === "typing") {
        const uid = evt.user_id;
        // clear previous timer
        if (typingTimersRef.current[uid]) clearTimeout(typingTimersRef.current[uid]);
        if (evt.is_typing) {
          setTypingUsers((t) => ({ ...t, [uid]: evt.name }));
          typingTimersRef.current[uid] = setTimeout(() => {
            setTypingUsers((t) => {
              const c = { ...t };
              delete c[uid];
              return c;
            });
          }, 3500);
        } else {
          setTypingUsers((t) => {
            const c = { ...t };
            delete c[uid];
            return c;
          });
        }
      }
    };
  }, [requestId]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      Object.values(typingTimersRef.current).forEach(clearTimeout);
      typingTimersRef.current = {};
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        // prevent auto-reconnect on intentional close
        ws.onclose = null;
        try { ws.close(); } catch {}
      }
    };
  }, [connect]);

  const sendMessage = useCallback((text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify({ type: "message", text }));
    return true;
  }, []);

  const sendTyping = useCallback((isTyping) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify({ type: "typing", is_typing: !!isTyping }));
    } catch {}
  }, []);

  return { messages, connected, typingUsers, sendMessage, sendTyping };
}
